import React, { useEffect, useCallback, useRef } from 'react';
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress
} from '@mui/material';
import { LoginButton } from './LoginButton';
import { apiRequest } from '../authConfig';
import apiService from '../services/apiService';
import api, { setAuthToken as setApiAuthToken } from '../services/api';

// Token refresh interval: 45 minutes (tokens typically expire after 60 min)
const TOKEN_REFRESH_INTERVAL_MS = 45 * 60 * 1000;

export const AuthWrapper = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();
  const refreshIntervalRef = useRef(null);
  const lastRefreshRef = useRef(Date.now());

  // Centralized token refresh function
  const refreshToken = useCallback(async (showToast = false) => {
    if (!accounts || accounts.length === 0) return false;

    const request = {
      ...apiRequest,
      account: accounts[0]
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      if (response?.accessToken) {
        console.log('[AuthWrapper] Token refreshed successfully');
        apiService.setAuthToken(response.accessToken);
        setApiAuthToken(response.accessToken);
        lastRefreshRef.current = Date.now();
        return true;
      }
    } catch (silentErr) {
      console.warn('[AuthWrapper] Silent token refresh failed:', silentErr?.message);

      // Try popup as fallback (only if user interaction is possible)
      if (document.visibilityState === 'visible' && !window.__auth_popup_in_progress) {
        try {
          window.__auth_popup_in_progress = true;
          if (showToast) {
            window.dispatchEvent(new CustomEvent('hrapp.toast', {
              detail: { sev: 'info', msg: 'Session refresh required...' }
            }));
          }
          const popupResponse = await instance.acquireTokenPopup(request);
          if (popupResponse?.accessToken) {
            console.log('[AuthWrapper] Token acquired via popup');
            apiService.setAuthToken(popupResponse.accessToken);
            setApiAuthToken(popupResponse.accessToken);
            lastRefreshRef.current = Date.now();
            return true;
          }
        } catch (popupErr) {
          console.error('[AuthWrapper] Popup token acquisition failed:', popupErr?.message);
          // Show reload suggestion
          window.dispatchEvent(new CustomEvent('hrapp.toast', {
            detail: {
              sev: 'warning',
              msg: 'Session expired. Please refresh the page to continue.'
            }
          }));
          return false;
        } finally {
          window.__auth_popup_in_progress = false;
        }
      }
    }
    return false;
  }, [instance, accounts]);

  // Initial token acquisition
  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      refreshToken(false);
    }
  }, [isAuthenticated, accounts, refreshToken]);

  // Proactive token refresh every 45 minutes
  useEffect(() => {
    if (!isAuthenticated || accounts.length === 0) {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    console.log('[AuthWrapper] Starting proactive token refresh interval');
    refreshIntervalRef.current = setInterval(() => {
      console.log('[AuthWrapper] Proactive token refresh triggered');
      refreshToken(false);
    }, TOKEN_REFRESH_INTERVAL_MS);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, accounts, refreshToken]);

  // Visibility change handler - refresh token when user returns to tab
  useEffect(() => {
    if (!isAuthenticated || accounts.length === 0) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
        // If more than 30 minutes since last refresh, refresh now
        if (timeSinceLastRefresh > 30 * 60 * 1000) {
          console.log('[AuthWrapper] Tab became visible, refreshing token (idle for',
            Math.round(timeSinceLastRefresh / 60000), 'min)');
          await refreshToken(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, accounts, refreshToken]);

  // Show loading spinner while authentication is in progress
  if (inProgress === "login") {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Signing you in...
        </Typography>
      </Container>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            HR Management System
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Please sign in with your Microsoft account to access the HR system.
          </Typography>
          <LoginButton />
        </Paper>
      </Container>
    );
  }

  // Show the main application if authenticated
  return children;
};
